import { createContext, useContext } from "react";
import { z } from "zod";
import type { OnboardingSelectGroup } from "./onboardingCatalog";

const optionSchema = z.object({
  id: z.string().min(1), label: z.string().min(1), position: z.number().int().nonnegative(), active: z.boolean(),
}).strict();
export const onboardingLiveCatalogSchema = z.object({
  schema: z.literal("onboarding_catalog.v1"), version: z.string().regex(/^[1-9]\d*$/),
  categories: z.array(optionSchema), advantages: z.array(optionSchema),
}).strict().superRefine((catalog, ctx) => {
  for (const key of ["categories", "advantages"] as const) {
    if (new Set(catalog[key].map((option) => option.id)).size !== catalog[key].length) {
      ctx.addIssue({ code: "custom", path: [key], message: "目录 ID 重复" });
    }
  }
});
export type OnboardingLiveCatalog = z.infer<typeof onboardingLiveCatalogSchema>;
export interface OnboardingCatalogGroups {
  categories: readonly OnboardingSelectGroup[];
  advantages: readonly OnboardingSelectGroup[];
}
export const emptyOnboardingCatalog: OnboardingCatalogGroups = { categories: [], advantages: [] };
export const OnboardingCatalogContext = createContext<OnboardingCatalogGroups>(emptyOnboardingCatalog);
export const useOnboardingCatalogGroups = () => useContext(OnboardingCatalogContext);

export function buildOnboardingCatalogGroups(catalog: OnboardingLiveCatalog | undefined, selectedCategories: readonly string[], selectedAdvantages: readonly string[]): OnboardingCatalogGroups {
  if (!catalog) return emptyOnboardingCatalog;
  const group = (kind: "categories" | "advantages", selected: readonly string[]): OnboardingSelectGroup[] => [{
    id: kind, label: kind === "categories" ? "品类" : "优势",
    options: catalog[kind].filter((option) => option.active || selected.includes(option.id))
      .sort((a, b) => a.position - b.position || a.id.localeCompare(b.id))
      .map((option) => ({ id: option.id, label: option.active ? option.label : `${option.label}（已停用）` })),
  }];
  return { categories: group("categories", selectedCategories), advantages: group("advantages", selectedAdvantages) };
}
